import {
  type ReactNode,
  createRef,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';

import { WidgetContainer } from '@pulse-web-ui/containers';
import { HelperText } from '@pulse-web-ui/helper-text';

import {
  BackButton,
  OTP_INPUTS_NUM,
  WidgetHeaderWithSubText,
  addTestAttribute,
  useStores,
} from '@shared/index';

import type { OtpType } from '../../model';
import { Resend } from '../resend';
import {
  HelperTextMessageContainer,
  InputSeparator,
  OtpController,
  StyledOtpInput,
} from './otp.styles';

type OtpProps = {
  code: string;
  onCodeChange: (code: string) => void;
  hasError: boolean;
  otpType: OtpType;
  errorText?: string;
  helperTextError?: string;
  onErrorResend?: () => void;
  onResend?: () => void;
  disabled?: boolean;
  subTitle?: string | ReactNode;
  phone?: string;
  goBack?: () => void;
  title?: string;
  isWithContainer?: boolean;
};

export const Otp = ({
  title,
  isWithContainer = true,
  code,
  subTitle,
  onCodeChange,
  hasError,
  errorText,
  helperTextError,
  disabled,
  phone,
  onErrorResend,
  onResend,
  goBack,
  otpType,
}: OtpProps) => {
  const { t } = useTranslation();
  const inputRefs = useRef<HTMLInputElement[]>([]);
  inputRefs.current = new Array(OTP_INPUTS_NUM).map(
    (_, i) => inputRefs.current[i] ?? createRef()
  );
  const {
    MainStore: {
      agentUserStore: { setConfirmToken },
    },
  } = useStores();

  useEffect(() => {
    if (!!hasError && !!inputRefs.current?.length) {
      inputRefs.current.forEach((input, index) => {
        if (index === 0) {
          input.focus();
        } else {
          input.blur();
        }
      });
    }
  }, [hasError]);

  const Container = useCallback(
    ({ children }: { children: ReactNode }) =>
      isWithContainer ? (
        <WidgetContainer>{children}</WidgetContainer>
      ) : (
        <>{children}</>
      ),
    [isWithContainer]
  );

  return (
    <Container>
      {goBack && <BackButton goBack={goBack} />}
      <WidgetHeaderWithSubText
        title={title || t('OTP:headers.enterCode')}
        subTitle={subTitle}
      />
      <OtpController>
        <HelperText
          status={hasError || helperTextError ? 'error' : 'default'}
          message={
            hasError || helperTextError ? (
              <HelperTextMessageContainer>
                {errorText || helperTextError || t('OTP:errors.incorrectCode')}
              </HelperTextMessageContainer>
            ) : (
              ''
            )
          }
        >
          <OtpInput
            value={code}
            shouldAutoFocus
            numInputs={OTP_INPUTS_NUM}
            renderSeparator={<InputSeparator />}
            renderInput={({ ref, ...props }, index) => (
              <StyledOtpInput
                ref={(instance) => {
                  if (instance) {
                    inputRefs.current[index] = instance;
                  }
                  ref(instance);
                }}
                disabled={disabled}
                $error={hasError}
                {...props}
                key={index}
                type='text'
                {...addTestAttribute(`otp.input.${index}`)}
              />
            )}
            onChange={onCodeChange}
            inputType='number'
          />
        </HelperText>
      </OtpController>
      <Resend
        otpType={otpType}
        onSuccess={setConfirmToken}
        onError={onErrorResend}
        onResend={onResend}
        isRequestLimitExceeded={!!helperTextError}
        phone={phone}
      />
    </Container>
  );
};
